import { Button, Loader } from '@mantine/core';
import { Spotlight } from '@mantine/spotlight';
import { Search } from 'lucide-react';
import { ReactNode, useState } from 'react';

import SelectableItem from '@/components/SelectableItem.tsx';
import { CustomSpotlight } from '@/components/custom-spotlight.tsx';
import PatientRow from '@/components/searchPatient/PatientRow.tsx';
import { patientSpotlightStore, patientSpotlightUtils } from '@/components/searchPatient/patient-spotlight-store.ts';

import usePatientSearch from '@/hooks/usePatientSearch.ts';

import { GenericPatient } from '@/models/GenericPatient.ts';

import { WalkInModalMode } from '@/stores/walkInStore.ts';

import { openAppointmentScheduler } from '@/utils/modal/appointmentSchedulerUtils.tsx';
import { openPatientModal, openWalkInModal } from '@/utils/modalUtils.tsx';
import { getPatientIdFromUser } from '@/utils/patientUtils.ts';

export const SearchPatientSpotlight = () => {
    const [inputValue, setInputValue] = useState('');

    const onPatientSelect = (patient: GenericPatient | undefined): void => {
        if (!patient) return;

        openPatientModal(
            patient.pvs_user_id,
            patient.eterno_user_id,
            patient.instance_id,
            `${patient.personal.last_name} ${patient.personal.first_name}`
        );
        patientSpotlightUtils.close();
    };

    const {
        data,
        is_loading: isLoading,
        is_validating: isValidating,
        handleSelect,
        onChangeSearch,
        clean,
        search_string: searchString,
    } = usePatientSearch(onPatientSelect);

    const handleInputChange = (value: string): void => {
        setInputValue(value);
        onChangeSearch(value);
    };

    const handleSpotlightOpen = (): void => {
        clean();
        setInputValue('');
    };

    // Determine if we should show the create appointment button
    const shouldShowCreateButton =
        Boolean(searchString) && !isLoading && (!data?.patients || data.patients.length === 0);

    const renderActionsContent = (): ReactNode => {
        if (data?.patients && data.patients.length > 0) {
            const validPatients = data.patients.filter((p) => getPatientIdFromUser(p.user));

            return (
                <div className="flex flex-col gap-3 p-2">
                    {validPatients.map((patient) => {
                        const patientId = getPatientIdFromUser(patient.user)!;
                        return (
                            <Spotlight.Action
                                key={patientId}
                                onClick={() => handleSelect(patientId)}
                                classNames={{
                                    action: 'block !bg-transparent !p-0 data-[selected]:!bg-transparent data-[selected]:shadow-lg',
                                }}
                            >
                                <SelectableItem isSelected={false} onClick={() => {}}>
                                    <PatientRow matches={patient.matches} patient={patient.user} />
                                </SelectableItem>
                            </Spotlight.Action>
                        );
                    })}
                </div>
            );
        }

        if (isLoading) {
            return (
                <div className="flex h-48 w-full items-center justify-center">
                    <Loader />
                </div>
            );
        }

        return (
            <Spotlight.Empty>
                <div className="flex flex-col items-center gap-4">
                    <p className="text-center text-slate-700">
                        {shouldShowCreateButton
                            ? 'Keine Patienten gefunden'
                            : 'Name, Geburtsdatum, Email oder Patientennummer angeben'}
                    </p>
                    {shouldShowCreateButton && (
                        <Button
                            onClick={() => {
                                openWalkInModal(
                                    () => openAppointmentScheduler({ mode: 'book' }),
                                    undefined,
                                    WalkInModalMode.CREATE_PATIENT
                                );
                                patientSpotlightUtils.close();
                            }}
                            variant="filled"
                        >
                            Neuen Patienten erstellen
                        </Button>
                    )}
                </div>
            </Spotlight.Empty>
        );
    };

    return (
        <CustomSpotlight
            id="patient-search-spotlight"
            closeOnActionTrigger
            spotlightStore={patientSpotlightStore}
            spotLightUtils={patientSpotlightUtils}
            onSpotlightOpen={handleSpotlightOpen}
        >
            <Spotlight.Search
                placeholder="Name, Geburtstagsdatum, Email, Patientennummer"
                leftSection={isLoading || isValidating ? <Loader size="xs" /> : <Search className="size-5" />}
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
            />
            <div className="mb-2 max-h-[70vh] overflow-y-auto bg-slate-100">
                <Spotlight.ActionsList>{renderActionsContent()}</Spotlight.ActionsList>
            </div>
        </CustomSpotlight>
    );
};
